import React, { useState } from 'react';
import { Check, AlertCircle, ExternalLink, FileText, ClipboardList, Info } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import Navigation from './Navigation';
import Footer from './Footer';
import RegistrationForm from './RegistrationForm';
import PricingFlyer from './registration/PricingFlyer';
import { registrationPricing, nflFlagPartnership } from '../mock';
import '../RegistrationForm.css';

const MYREC_URL = 'https://libertyroadmd.myrec.com/info/activities/program_details.aspx?ProgramID=29897';

const RegistrationPage = () => {
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);

  const handleExternalRegister = () => {
    window.open(MYREC_URL, '_blank', 'noopener,noreferrer');
  };

  const handleExpressInterest = () => {
    setShowRegistrationForm(true);
  };

  const programs = [
    { name: 'Tackle Football', ageRange: 'Ages 7u-13u', pricing: registrationPricing.tackle },
    { name: 'Flag Football', ageRange: 'Ages 5-6', pricing: registrationPricing.flag },
    { name: 'Girls Flag Football', ageRange: 'Ages 12-14 · NEW', pricing: registrationPricing.girlsFlag },
    { name: 'Cheerleading', ageRange: 'Ages 5-14', pricing: registrationPricing.cheer },
    { name: 'Spring NFL FLAG', ageRange: 'Ages 5-12', pricing: registrationPricing.springFlag },
  ];

  return (
    <div className="page-container">
      <Navigation />
      
      <main className="main-content">
        <section className="page-hero">
          <div className="container">
            <h1 className="page-title">Registration</h1>
            <p className="page-subtitle">Join the Panthers for the 2026 Season</p>
          </div>
        </section>

        {/* NFL FLAG Banner */}
        <section className="registration-banner">
          <div className="container">
            <div className="nfl-flag-registration">
              <h2>NFL FLAG Football - NEW!</h2>
              <p>Season: {nflFlagPartnership.seasonDates}</p>
            </div>
          </div>
        </section>

        {/* Price Flyer (auto-generated from live pricing; falls back to image if provided) */}
        <section className="flyer-section" data-testid="price-flyer">
          <div className="container">
            <div className="flyer-container">
              <PricingFlyer flyerImage={registrationPricing.flyerImage} />
            </div>
          </div>
        </section>

        {/* My Rec Notice */}
        <section className="myrec-notice-section" data-testid="myrec-notice">
          <div className="container">
            <Card className="myrec-notice-card">
              <CardContent>
                <div className="myrec-notice-inner">
                  <Info size={28} className="myrec-notice-icon" />
                  <div className="myrec-notice-text">
                    <h3>All registrations are processed through My Rec</h3>
                    <p>
                      Player information, waivers, and payment are all handled on the
                      Liberty Road Recreation & Park Council&apos;s My Rec portal.
                      Click <strong>&ldquo;Register on My Rec&rdquo;</strong> below to get started.
                    </p>
                  </div>
                  <Button
                    className="btn-primary myrec-notice-btn"
                    onClick={handleExternalRegister}
                    data-testid="myrec-notice-cta"
                  >
                    Register on My Rec <ExternalLink size={18} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Pricing Grid */}
        <section className="pricing-section">
          <div className="container">
            <h2 className="section-title">2026 Season Pricing</h2>
            <p className="section-subtitle">{registrationPricing.note}</p>
            <div className="pricing-grid">
              {programs.map((program) => (
                <Card key={program.name} className="pricing-card" data-testid={`pricing-card-${program.name}`}>
                  <CardHeader>
                    <CardTitle>{program.name}</CardTitle>
                    <div className="age-range">{program.ageRange}</div>
                  </CardHeader>
                  <CardContent>
                    <div className="pricing-tiers">
                      {program.pricing.map((tier) => (
                        <div key={`${program.name}-${tier.period}`} className="pricing-tier">
                          <span className="tier-period">{tier.period}</span>
                          <span className="tier-price">${tier.price}</span>
                        </div>
                      ))}
                    </div>
                    <Button
                      className="btn-primary"
                      onClick={handleExternalRegister}
                      data-testid={`register-myrec-${program.name}`}
                    >
                      Register on My Rec <ExternalLink size={16} />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* State ID Requirement Section */}
        <section className="state-id-section" data-testid="state-id-section">
          <div className="container">
            <Card className="state-id-card">
              <CardContent>
                <div className="state-id-header">
                  <FileText size={28} />
                  <h3>State ID Requirement</h3>
                </div>
                <p className="state-id-description">
                  All players must have a valid <strong>Maryland State ID</strong> before the start of the season. 
                  If your child does not already have a state ID, please obtain one prior to the first practice.
                </p>
                <div className="state-id-info">
                  <div className="info-item">
                    <span className="info-label">Where to get it:</span>
                    <span className="info-value">Any Maryland MVA office</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">What to bring:</span>
                    <span className="info-value">Birth certificate & proof of residency</span>
                  </div>
                </div>
                <p className="state-id-note">
                  <AlertCircle size={16} />
                  Players without a valid state ID will not be eligible to participate in games.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Discounts & Policy */}
        <section className="policy-section">
          <div className="container">
            <div className="policies-grid">
              {/* Discounts */}
              <Card className="policy-card discount-card">
                <CardContent>
                  <div className="policy-header">
                    <Check size={24} />
                    <h3>Registration Discounts</h3>
                  </div>
                  <p><strong>Sibling Discount:</strong> $25 off after registration of the first child.</p>
                </CardContent>
              </Card>

              {/* Refund Policy */}
              <Card className="policy-card">
                <CardContent>
                  <div className="policy-header">
                    <AlertCircle size={24} />
                    <h3>Refund Policy</h3>
                  </div>
                  <p>No refunds will be issued after the first week of practice. Any refund that is issued will be subject to a <strong>$10 administrative fee</strong>.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Register CTA */}
        <section className="register-cta-section">
          <div className="container">
            <div className="register-cta">
              <h2>Ready to Join?</h2>
              <p>
                Registration is handled on the My Rec portal. Not sure yet?
                Use &ldquo;Get More Info&rdquo; and a Panthers coordinator will reach out.
              </p>
              <div className="cta-buttons">
                <Button
                  className="btn-primary btn-large"
                  onClick={handleExternalRegister}
                  data-testid="register-now-btn"
                >
                  Register on My Rec <ExternalLink size={20} />
                </Button>
                <Button
                  className="btn-secondary btn-large"
                  onClick={handleExpressInterest}
                  data-testid="express-interest-btn"
                >
                  <ClipboardList size={20} />
                  Get More Info
                </Button>
              </div>
              <p className="cta-fineprint">
                Optional: the &ldquo;Get More Info&rdquo; form lets us contact you about
                the season &mdash; it does <strong>not</strong> register your child.
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      
      {/* Registration Form Modal */}
      {showRegistrationForm && (
        <div className="registration-modal-overlay" onClick={() => setShowRegistrationForm(false)}>
          <div className="registration-modal" onClick={(e) => e.stopPropagation()}>
            <button 
              className="modal-close-btn"
              onClick={() => setShowRegistrationForm(false)}
            >
              ✕
            </button>
            <RegistrationForm 
              onClose={() => setShowRegistrationForm(false)}
              onSuccess={(data) => {
                // Keep modal open to show success state
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrationPage;
